// GA送信周りの処理

import { DESIGN_ACTION, type DesignActionType, type PaymentMethodType, type PurchaseRouteFields } from '@/constants';
import { toPostHogRoute } from '@/utils/purchaseRoute';
import { EVENT_NAMES, trackEvent } from '@monorepo/common';

// データレイヤーを取得する共通関数
export const getDataLayer = () => {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  (window as any).dataLayer = (window as any).dataLayer || [];
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const dataLayer = (window as any).dataLayer;
  return dataLayer;
};

// ※ユーザーIDのみuser_idとスネークケースなのは、GA上で自動で付与されるuserIdと被らないようにするため

/**
 * ecommerceイベント送信時の注意事項
 *
 * GA4では、ecommerceイベントを送信する前に前回のecommerceオブジェクトをクリアすることが推奨されています。
 * Next.jsはSPAとして動作するため、ページ遷移してもwindow.dataLayerが保持され、
 * 古いecommerceデータが残ったままになる可能性があります。
 *
 * 各ecommerceイベント関数では、以下のパターンでクリア処理を実施しています：
 * ```typescript
 * getDataLayer().push({ ecommerce: null });
 * getDataLayer().push({ event: 'xxx', ecommerce: { ... } });
 * ```
 *
 * 参考: https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm&hl=ja
 */

/**
 * GA4推奨イベントの商品アイテムパラメーターの型
 * 詳しい説明は以下を参照
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events?sjid=10436666439109070913-NC&hl=ja&client_type=gtag#purchase_item
 */
// 購入経路（PurchaseRouteFields）は item スコープ。GA4 の items[] に乗せて経路別の売上を見る
export type PurchaseItem = {
  item_id: string;
  item_name: string;
  item_brand: string;
  item_variant?: string;
  price?: number;
  quantity?: number;
  material_ids?: string;
} & PurchaseRouteFields;

const pushDesignActionEvent = (action: DesignActionType, params: Record<string, string | number>) => {
  getDataLayer().push({
    event: `design_tool_${action.replace(/-/g, '_')}`,
    ...params,
  });
};

// デザインツール - パーツ切替
export const sendDesignSwitchPartEvent = (params: {
  productId: number;
  partId: number;
  partName: string;
  workId: number;
  userId: number;
}) => {
  pushDesignActionEvent(DESIGN_ACTION.switchPart, {
    productId: params.productId,
    partId: params.partId,
    partName: params.partName,
    workId: params.workId,
    user_id: params.userId,
  });
};

// デザインツール - 素材選択
export const sendDesignSelectMaterialEvent = (params: {
  materialId: number;
  materialName: string;
  productId: number;
  workId: number;
  userId: number;
}) => {
  pushDesignActionEvent(DESIGN_ACTION.selectMaterial, {
    materialId: params.materialId,
    materialName: params.materialName,
    productId: params.productId,
    workId: params.workId,
    user_id: params.userId,
  });
};

// デザインツール - コマ削除（キャンバスのゴミ箱・コマ追加モーダルの両方から呼ぶ）
export const sendDesignDeleteMaterialEvent = (params: {
  materialId: number;
  productId: number;
  partId: number;
  workId: number;
  userId: number;
}) => {
  pushDesignActionEvent(DESIGN_ACTION.deleteMaterial, {
    materialId: params.materialId,
    productId: params.productId,
    partId: params.partId,
    workId: params.workId,
    user_id: params.userId,
  });
};

// デザインツール - プレビューを開く
export const sendDesignOpenPreviewEvent = (params: { productId: number; workId: number; userId: number }) => {
  pushDesignActionEvent(DESIGN_ACTION.openPreview, {
    productId: params.productId,
    workId: params.workId,
    user_id: params.userId,
  });
};

// デザインツール - プレビューを閉じる
export const sendDesignClosePreviewEvent = (params: { productId: number; workId: number; userId: number }) => {
  pushDesignActionEvent(DESIGN_ACTION.closePreview, {
    productId: params.productId,
    workId: params.workId,
    user_id: params.userId,
  });
};

// デザインツール - 商品カラー変更
export const sendDesignChangeColorEvent = (params: {
  productId: number;
  colorId: number;
  colorName: string;
  workId: number;
  userId: number;
}) => {
  pushDesignActionEvent(DESIGN_ACTION.changeColor, {
    productId: params.productId,
    colorId: params.colorId,
    colorName: params.colorName,
    workId: params.workId,
    user_id: params.userId,
  });
};

// デザインツール - 最初の状態に戻す
export const sendDesignResetToInitialEvent = (params: {
  productId: number;
  partId: number;
  workId: number;
  userId: number;
}) => {
  pushDesignActionEvent(DESIGN_ACTION.resetToInitial, {
    productId: params.productId,
    partId: params.partId,
    workId: params.workId,
    user_id: params.userId,
  });
};

// デザインツール - パーツの配置を全消し
export const sendDesignClearPartsEvent = (params: { productId: number; workId: number; userId: number }) => {
  pushDesignActionEvent(DESIGN_ACTION.clearParts, {
    productId: params.productId,
    workId: params.workId,
    user_id: params.userId,
  });
};

// デザインツール - 素材サイズ最大
export const sendDesignSizeMaxEvent = (params: {
  materialId: number;
  productId: number;
  partId: number;
  workId: number;
  userId: number;
}) => {
  pushDesignActionEvent(DESIGN_ACTION.sizeMax, {
    materialId: params.materialId,
    productId: params.productId,
    partId: params.partId,
    workId: params.workId,
    user_id: params.userId,
  });
};

// デザインツール - 素材サイズ最小
export const sendDesignSizeMinEvent = (params: {
  materialId: number;
  productId: number;
  partId: number;
  workId: number;
  userId: number;
}) => {
  pushDesignActionEvent(DESIGN_ACTION.sizeMin, {
    materialId: params.materialId,
    productId: params.productId,
    partId: params.partId,
    workId: params.workId,
    user_id: params.userId,
  });
};

// デザインツール - 素材の回転をリセット
export const sendDesignRotationResetEvent = (params: {
  materialId: number;
  productId: number;
  partId: number;
  workId: number;
  userId: number;
}) => {
  pushDesignActionEvent(DESIGN_ACTION.rotationReset, {
    materialId: params.materialId,
    productId: params.productId,
    partId: params.partId,
    workId: params.workId,
    user_id: params.userId,
  });
};

// デザインツール - キャンバス拡大
export const sendDesignCanvasZoomInEvent = (params: {
  productId: number;
  partId: number;
  workId: number;
  userId: number;
}) => {
  pushDesignActionEvent(DESIGN_ACTION.canvasZoomIn, {
    productId: params.productId,
    partId: params.partId,
    workId: params.workId,
    user_id: params.userId,
  });
};

// デザインツール - キャンバス縮小
export const sendDesignCanvasZoomOutEvent = (params: {
  productId: number;
  partId: number;
  workId: number;
  userId: number;
}) => {
  pushDesignActionEvent(DESIGN_ACTION.canvasZoomOut, {
    productId: params.productId,
    partId: params.partId,
    workId: params.workId,
    user_id: params.userId,
  });
};

// デザインツール - グリッド表示切替
export const sendDesignToggleGridEvent = (params: {
  productId: number;
  partId: number;
  workId: number;
  userId: number;
}) => {
  pushDesignActionEvent(DESIGN_ACTION.toggleGrid, {
    productId: params.productId,
    partId: params.partId,
    workId: params.workId,
    user_id: params.userId,
  });
};

// デザインツール - 素材モーダルを開く
export const sendDesignOpenMaterialModalEvent = (params: {
  productId: number;
  partId: number;
  workId: number;
  userId: number;
}) => {
  pushDesignActionEvent(DESIGN_ACTION.openMaterialModal, {
    productId: params.productId,
    partId: params.partId,
    workId: params.workId,
    user_id: params.userId,
  });
};

// デザインツール - ロゴガイドラインを開く
export const sendDesignOpenLogoGuidelineEvent = (params: { productId: number; workId: number; userId: number }) => {
  pushDesignActionEvent(DESIGN_ACTION.openLogoGuideline, {
    productId: params.productId,
    workId: params.workId,
    user_id: params.userId,
  });
};

// デザインツール - 戻る
export const sendDesignBackEvent = (params: { productId: number; workId: number; userId: number }) => {
  pushDesignActionEvent(DESIGN_ACTION.back, {
    productId: params.productId,
    workId: params.workId,
    user_id: params.userId,
  });
};

// デザインツール - 素材フィルタタブ切替
export const sendDesignSelectMaterialTabEvent = (params: { filterTab: string; workId: number; userId: number }) => {
  pushDesignActionEvent(DESIGN_ACTION.selectMaterialTab, {
    filterTab: params.filterTab,
    workId: params.workId,
    user_id: params.userId,
  });
};

// 作品を選ぶ - TOPページからの遷移
export const sendWorkNavigationFromTopEvent = (workId: number, workName: string, user_id: number) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'work_navigation_from_top',
    workId,
    workName,
    user_id,
  });
};

// 作品一覧 - 表示形式切り替え
export const sendWorksViewModeSwitchEvent = (workViewMode: string, user_id: number) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'works_view_mode_switch',
    workViewMode,
    user_id,
  });
};

// 素材を選ぶ - アイテム選択クリック
export const sendSelectItemEvent = (items: PurchaseItem[]) => {
  // GA4推奨イベント
  getDataLayer().push({ ecommerce: null });
  getDataLayer().push({
    event: 'select_item',
    ecommerce: {
      items: items,
    },
  });
};

// 商品を閲覧
export const sendViewItemEvent = (items: PurchaseItem[]) => {
  // GA4推奨イベント
  getDataLayer().push({ ecommerce: null });
  getDataLayer().push({
    event: 'view_item',
    ecommerce: {
      items: items,
    },
  });
};

// 商品リストを閲覧
export const sendViewItemListEvent = (items: PurchaseItem[], item_list_name: string) => {
  // GA4推奨イベント
  getDataLayer().push({ ecommerce: null });
  getDataLayer().push({
    event: 'view_item_list',
    ecommerce: {
      currency: 'JPY',
      item_list_name: item_list_name,
      items: items,
    },
  });
};

// チェックアウト開始
export const sendBeginCheckoutEvent = (items: PurchaseItem[], value: number) => {
  // GA4推奨イベント
  getDataLayer().push({ ecommerce: null });
  getDataLayer().push({
    event: 'begin_checkout',
    ecommerce: {
      currency: 'JPY',
      //ドキュメントでは税抜推奨だが、一番最初に実装したpurchaseイベントでは税込金額を使用しているため、整合性をとるためこちらも税込金額を使用する。
      value: value,
      items: items,
    },
  });
};

// 素材を選ぶ - お気に入りクリック
export const sendMaterialFavoriteClickEvent = (
  materialId: number,
  materialName: string,
  isFavorite: boolean,
  user_id: number,
) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'material_favorite_click',
    materialId,
    materialName,
    isFavorite,
    user_id,
  });
};

// アイテムのオプション、個数の決定 - デザインに進むボタンクリック
export const sendDesignStartButtonClickEvent = (
  colorName: string,
  sizeName: string,
  quantity: number,
  user_id: number,
) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'design_start_button_click',
    colorName,
    sizeName,
    quantity,
    user_id,
  });
};

// カートに商品を追加
// PostHog の add_to_cart は「投入アクション数」で経路別ファネルの母数になるため、
// カート内の数量変更（既存行の編集＝新規投入でない）では trackInPostHog:false にして GA4 のみ送る。
export const sendAddToCartEvent = (
  items: PurchaseItem[],
  { trackInPostHog = true, isGuest = false }: { trackInPostHog?: boolean; isGuest?: boolean } = {},
) => {
  // GA4推奨イベント
  const value = items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 0), 0);
  getDataLayer().push({ ecommerce: null });
  getDataLayer().push({
    event: 'add_to_cart',
    ecommerce: {
      currency: 'JPY',
      value: value,
      items: items,
    },
    isGuest,
  });
  if (!trackInPostHog) return;
  trackEvent(EVENT_NAMES.addToCart, {
    value,
    productIds: items.map((item) => item.item_id),
    quantity: items.reduce((sum, item) => sum + (item.quantity || 0), 0),
    isGuest,
    ...toPostHogRoute(items[0] ?? {}),
  });
};

// カートから商品を削除
export const sendRemoveFromCartEvent = (items: PurchaseItem[]) => {
  // GA4推奨イベント
  const value = items.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 0), 0);
  getDataLayer().push({ ecommerce: null });
  getDataLayer().push({
    event: 'remove_from_cart',
    ecommerce: {
      currency: 'JPY',
      value: value,
      items: items,
    },
  });
};

// カートを閲覧
export const sendViewCartEvent = (items: PurchaseItem[], value: number) => {
  // GA4推奨イベント
  getDataLayer().push({ ecommerce: null });
  getDataLayer().push({
    event: 'view_cart',
    ecommerce: {
      currency: 'JPY',
      value: value,
      items: items,
    },
  });
};

// FAQ - 詳細アコーディオンクリック
export const sendFaqAccordionClickEvent = (faqTitle: string, user_id: number) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'faq_accordion_click',
    faqTitle,
    user_id,
  });
};

// 購入イベント
export const sendPurchaseGAEvent = (
  orderId: string,
  totalAmount: number,
  items: PurchaseItem[],
  isGuest = false,
  paymentMethod?: PaymentMethodType,
) => {
  // GA4推奨イベント
  getDataLayer().push({ ecommerce: null });
  getDataLayer().push({
    event: 'purchase',
    ecommerce: {
      transaction_id: orderId,
      value: totalAmount, //税込金額
      tax: 0,
      currency: 'JPY',
      items: items,
    },
    isGuest,
    paymentMethod,
  });
};

// みんなのデザイン - このデザインからグッズをつくる
export const sendClickUserDesignCreateGoodsEvent = (
  userPrintId: string | number,
  workId: number,
  workName: string,
  productId: number,
  productName: string,
  colorName: string,
  sizeName: string,
  materialIds: string,
) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'click_user_design_create_goods',
    userPrintId,
    workId,
    workName,
    productId,
    productName,
    colorName,
    sizeName,
    materialIds,
  });
};

// みんなのデザイン - 前後のデザインを入れ替える
export const sendClickShareDesignNavigationEvent = () => {
  // カスタムイベント
  getDataLayer().push({
    event: 'click_user_design_change',
  });
};

// カート - もう一度デザインする
export const sendClickCartRedesignEvent = (userPrintId: number) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'click_cart_redesign',
    userPrintId,
  });
};

// 注文履歴 - もう一度購入する
export const sendClickPurchaseHistoryRebuyEvent = (orderId: string, userPrintId: number) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'click_purchase_history_rebuy',
    orderId,
    userPrintId,
  });
};
// カート画面 - 購入画面へ進むボタンクリック
export const sendGoPurchaseEvent = (user_id: number) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'go_purchase',
    user_id,
  });
};

// 定型購入 - 「カートに追加」クリック
export const sendClickTemplatePurchaseEvent = (
  productId: number,
  productName: string,
  colorName: string,
  sizeName: string,
) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'click_template_purchase',
    productId,
    productName,
    colorName,
    sizeName,
  });
};

// カート画面 - 作品一覧へ遷移ボタンクリック
export const sendGoWorksFromCartEvent = (user_id: number) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'go_works_from_cart',
    user_id,
  });
};

// 購入認証3択 - ログインして購入するボタンクリック
export const sendClickLoginPurchaseGaEvent = () => {
  // カスタムイベント
  getDataLayer().push({
    event: 'click_login_purchase',
  });
};

// 購入認証3択 - 新規会員登録して購入するボタンクリック
export const sendClickSignupPurchaseGaEvent = () => {
  // カスタムイベント
  getDataLayer().push({
    event: 'click_signup_purchase',
  });
};

// 購入認証3択 - 登録せずにゲスト購入するボタンクリック
export const sendClickGuestPurchaseGaEvent = () => {
  // カスタムイベント
  getDataLayer().push({
    event: 'click_guest_purchase',
  });
};

// ゲスト購入後 - 会員登録完了（コンバージョン）
export const sendGuestConversionCompleteEvent = (user_id: number) => {
  // カスタムイベント
  getDataLayer().push({
    event: 'guest_conversion_complete',
    user_id,
  });
};
